import shortcutRegistry from "./shortcutRegistry.json";

export const APP_SHORTCUT_EVENT = "app:shortcut";
export const APP_ESCAPE_EVENT = "app:escape";

const KEY_ALIASES = {
  esc: "escape",
  return: "enter",
};

const KEY_TO_CODE = {
  "/": "slash",
  "?": "slash",
};

const KEY_TO_KEYCODE = {
  "/": 191,
  "?": 191,
  enter: 13,
  escape: 27,
};

export const shortcutItems = shortcutRegistry;

export const getShortcutById = (id) =>
  shortcutItems.find((shortcut) => shortcut.id === id) || null;

export const normalizeKey = (key = "") => {
  const value = String(key || "").trim().toLowerCase();
  return KEY_ALIASES[value] || value;
};

export const normalizeCode = (code = "") =>
  String(code || "").trim().toLowerCase();

const inferCodeFromKey = (key = "") => {
  const normalized = normalizeKey(key);
  if (/^[a-z]$/.test(normalized)) return `key${normalized}`;
  if (/^[0-9]$/.test(normalized)) return `digit${normalized}`;
  return KEY_TO_CODE[normalized] || "";
};

const inferKeyCodeFromKey = (key = "") => {
  const normalized = normalizeKey(key);
  if (/^[a-z]$/.test(normalized)) return normalized.toUpperCase().charCodeAt(0);
  if (/^[0-9]$/.test(normalized)) return normalized.charCodeAt(0);
  return KEY_TO_KEYCODE[normalized] || null;
};

export const isEditableTarget = (target) => {
  if (!target) return false;
  const tag = String(target.tagName || "").toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  return Boolean(target.isContentEditable);
};

const matchRoutePattern = (pattern, pathname) => {
  if (!pattern || pattern === "*") return true;
  if (pattern.endsWith("/*")) {
    const prefix = pattern.slice(0, -1);
    return pathname.startsWith(prefix);
  }
  return pathname === pattern;
};

const getRouteSpecificity = (shortcut) => {
  const patterns = Array.isArray(shortcut.routeMatch)
    ? shortcut.routeMatch
    : [shortcut.routeMatch];
  return patterns.reduce((max, pattern) => {
    if (!pattern || pattern === "*") return Math.max(max, 0);
    if (pattern.endsWith("/*")) return Math.max(max, 1);
    return Math.max(max, 2);
  }, 0);
};

export const matchesShortcutRoute = (shortcut, pathname) => {
  const patterns = Array.isArray(shortcut.routeMatch)
    ? shortcut.routeMatch
    : [shortcut.routeMatch];
  return patterns.some((pattern) => matchRoutePattern(pattern, pathname));
};

export const getActiveShortcuts = (pathname = "/") =>
  shortcutItems.filter((shortcut) => matchesShortcutRoute(shortcut, pathname));

export const getMatchingShortcut = (event, pathname = "/") => {
  const activeShortcuts = getActiveShortcuts(pathname).sort((left, right) => {
    return getRouteSpecificity(right) - getRouteSpecificity(left);
  });

  return activeShortcuts.find((shortcut) => {
    const key = normalizeKey(event.key);
    const shortcutKey = normalizeKey(shortcut.key);
    const eventCode = normalizeCode(event.code);
    const shortcutCode = normalizeCode(shortcut.code || inferCodeFromKey(shortcut.key));
    const eventKeyCode = Number(event.keyCode || event.which || 0) || null;
    const shortcutKeyCode =
      Number(shortcut.keyCode || inferKeyCodeFromKey(shortcut.key) || 0) || null;
    const keyMatches = key === shortcutKey;
    const codeMatches = shortcutCode && eventCode === shortcutCode;
    const keyCodeMatches = shortcutKeyCode && eventKeyCode === shortcutKeyCode;
    if (!keyMatches && !codeMatches && !keyCodeMatches) return false;

    const wantsCtrlOrCmd = Boolean(shortcut.ctrlOrCmd);
    const hasCtrlOrCmd = Boolean(event.ctrlKey || event.metaKey);
    if (wantsCtrlOrCmd !== hasCtrlOrCmd) return false;

    if (Boolean(shortcut.alt) !== Boolean(event.altKey)) return false;
    if (Boolean(shortcut.shift) !== Boolean(event.shiftKey)) return false;

    if (!wantsCtrlOrCmd && (event.ctrlKey || event.metaKey)) return false;

    return true;
  });
};

export const getShortcutSections = (pathname = "/") => {
  const ordered = [];
  const sections = new Map();

  getActiveShortcuts(pathname).forEach((shortcut) => {
    if (!sections.has(shortcut.section)) {
      sections.set(shortcut.section, []);
      ordered.push(shortcut.section);
    }
    sections.get(shortcut.section).push(shortcut);
  });

  return ordered.map((title) => ({
    title,
    items: sections.get(title) || [],
  }));
};

export const dispatchAppShortcut = (id, detail = {}) => {
  return window.dispatchEvent(
    new CustomEvent(APP_SHORTCUT_EVENT, {
      cancelable: true,
      detail: { id, ...detail },
    })
  );
};

export const dispatchAppEscape = (detail = {}) => {
  return window.dispatchEvent(
    new CustomEvent(APP_ESCAPE_EVENT, {
      cancelable: true,
      detail,
    })
  );
};

export const focusAndSelect = (element) => {
  if (!element) return false;
  element.focus?.();
  element.select?.();
  return true;
};

export const findVisibleElements = (selector) => {
  if (typeof document === "undefined") return [];
  return Array.from(document.querySelectorAll(selector)).filter((element) => {
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  });
};

export const focusFirstVisible = (selector) => {
  const [first] = findVisibleElements(selector);
  return focusAndSelect(first);
};

const isMacPlatform = () => {
  if (typeof navigator === "undefined") return false;
  const platform =
    navigator.userAgentData?.platform || navigator.platform || navigator.userAgent || "";
  return /mac|iphone|ipad|ipod/i.test(String(platform));
};

const formatMacToken = (token = "") => {
  switch (token) {
    case "ctrl/cmd":
    case "cmd/ctrl":
    case "cmd":
    case "command":
      return "⌘";
    case "ctrl":
    case "control":
      return "^";
    case "alt":
    case "option":
    case "alt/option":
      return "⌥";
    case "shift":
      return "⇧";
    case "enter":
      return "↵";
    case "escape":
    case "esc":
      return "Esc";
    default:
      return token.length === 1 ? token.toUpperCase() : token;
  }
};

const formatDefaultToken = (token = "") => {
  switch (token) {
    case "ctrl/cmd":
    case "cmd/ctrl":
      return "Ctrl";
    case "cmd":
    case "command":
      return "Cmd";
    case "alt/option":
      return "Alt";
    case "escape":
      return "Esc";
    default:
      return token.length === 1 ? token.toUpperCase() : token;
  }
};

export const formatShortcutCombo = (combo = "", options = {}) => {
  const raw = String(combo || "").trim();
  if (!raw) return "";

  const useMac = options.platform === "mac" || (options.platform !== "default" && isMacPlatform());
  const parts = raw.split("+").map((part) => part.trim().toLowerCase());

  if (useMac) {
    return parts.map((part) => formatMacToken(part)).join("");
  }

  return parts
    .map((part) => formatDefaultToken(part))
    .join("+");
};
